import { useState, useEffect } from 'react';
import { api } from '../../../services/api';

interface Modele {
  id: string;
  nom: string;
  categorie: string;
  image: string;
}

interface SelectedItem {
    type_habit_id: string;
    nom: string;
    quantite: number;
    modele_id?: string;
    modele_image?: string;
    modele_custom_file?: File;
}

interface Props {
  selectedItems: SelectedItem[];
  onChange: (items: SelectedItem[]) => void;
}

const CATEGORIES = ['Tous', 'Traditionnel', 'Moderne', 'Cérémonie', 'Casual', 'Homme'];

export default function StepModeleSelection({ selectedItems, onChange }: Props) {
  const [activeCateg, setActiveCateg] = useState('Tous');
  const [modeles, setModeles] = useState<Modele[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeItemIndex, setActiveItemIndex] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (token) {
        api.getModeles(token).then(data => {
            setModeles(data.map((m: any) => ({
                id: String(m.id),
                nom: m.nom,
                categorie: m.categorie_nom || 'Moderne',
                image: m.image_url
            })));
            setLoading(false);
        }).catch(() => setLoading(false));
    }
  }, []);

  const onSelect = (mId: string, mImage: string) => {
    const newItems = [...selectedItems];
    newItems[activeItemIndex] = {
        ...newItems[activeItemIndex],
        modele_id: mId,
        modele_image: mImage,
        modele_custom_file: undefined // Reset custom if selecting from list
    };
    onChange(newItems);
    
    // Auto-advance if possible
    if (activeItemIndex < selectedItems.length - 1) {
        setTimeout(() => setActiveItemIndex(prev => prev + 1), 300);
    }
  };

  const handleCapture = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
        const newItems = [...selectedItems];
        newItems[activeItemIndex] = {
            ...newItems[activeItemIndex],
            modele_id: undefined,
            modele_image: e.target?.result as string,
            modele_custom_file: file
        };
        onChange(newItems);
        // Auto-advance
        if (activeItemIndex < selectedItems.length - 1) {
            setTimeout(() => setActiveItemIndex(prev => prev + 1), 300);
        }
    };
    reader.readAsDataURL(file);
  };

  const skipModele = () => {
    const newItems = [...selectedItems];
    newItems[activeItemIndex] = {
        ...newItems[activeItemIndex],
        modele_id: undefined,
        modele_image: undefined,
        modele_custom_file: undefined
    };
    onChange(newItems);
    if (activeItemIndex < selectedItems.length - 1) {
        setActiveItemIndex(idx => idx + 1);
    }
  };

  const filtered = activeCateg === 'Tous' ? modeles : modeles.filter(m => m.categorie === activeCateg);

  if (selectedItems.length === 0) return <div className="p-8 text-center text-gray-500">Veuillez d'abord sélectionner des types de couture.</div>;

  // Calculer quels modèles sont sélectionnés par d'autres articles
  const alreadySelectedIds = selectedItems.map(item => item.modele_id).filter(id => !!id);

  const progress = Math.round(((activeItemIndex + 1) / selectedItems.length) * 100);

  return (
    <div className="space-y-6">
       {/* Navigation items */}
       {selectedItems.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {selectedItems.map((item, idx) => (
                  <button 
                    key={idx}
                    onClick={() => setActiveItemIndex(idx)}
                    className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap border-2 flex items-center gap-2 ${
                        activeItemIndex === idx ? 'bg-foret-500 text-white border-foret-500 shadow-md' : 'bg-white text-gray-400 border-gray-100 hover:border-gray-200'
                    }`}
                  >
                      {item.modele_image ? (
                        <div className="w-5 h-5 rounded-md overflow-hidden bg-gray-100">
                          <img src={item.modele_image.startsWith('data:') ? item.modele_image : api.getProxyUrl(item.modele_image)} className="w-full h-full object-cover" alt="" />
                        </div>
                      ) : <div className="w-5 h-5 rounded-md border border-dashed border-gray-300 flex items-center justify-center text-[10px]"><i className="ri-image-line" /></div>}
                      {item.nom}
                  </button>
              ))}
          </div>
       )}

      <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4 shadow-sm relative overflow-hidden">
          {/* Progress bar inside the box */}
          <div 
            className="absolute top-0 left-0 right-0 h-1 bg-gray-50" 
            role="progressbar" 
            aria-valuenow={progress} 
            aria-valuemin={0} 
            aria-valuemax={100} 
            aria-label="Progression de la sélection des modèles"
          >
            <div 
                className="h-full bg-foret-300 transition-all duration-500" 
                style={{ width: `${progress}%` } as React.CSSProperties} 
            />
          </div>

          <div className="flex items-center justify-between pt-2">
              <div>
                  <h3 className="font-bold text-gray-900 leading-tight">Modèle pour : {selectedItems[activeItemIndex].nom}</h3>
                  <p className="text-[10px] text-gray-400 uppercase font-black tracking-widest mt-1">Article {activeItemIndex + 1} sur {selectedItems.length}</p>
              </div>
              <button 
                onClick={skipModele}
                className="text-xs font-bold text-gray-400 hover:text-gray-600 px-3 py-1.5 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors"
              >
                  Ignorer
              </button>
          </div>

          <div className="flex items-center gap-4 py-4 border-y border-gray-50">
                <label className="flex-1 cursor-pointer group">
                    <input 
                        type="file" 
                        accept="image/*" 
                        capture="environment" 
                        className="hidden" 
                        onChange={e => e.target.files?.[0] && handleCapture(e.target.files[0])}
                        aria-label="Prendre une photo avec l'appareil"
                    />
                    <div className="flex flex-col items-center justify-center p-4 rounded-2xl border-2 border-dashed border-gray-200 group-hover:border-foret-400 group-hover:bg-foret-50 transition-all">
                        <i className="ri-camera-lens-line text-2xl text-gray-400 group-hover:text-foret-500" />
                        <span className="text-[11px] font-bold text-gray-500 mt-2">Photo personnalisée</span>
                    </div>
                </label>

                {selectedItems[activeItemIndex].modele_image && (
                     <div className="w-24 h-24 rounded-2xl overflow-hidden border-2 border-foret-500 shadow-md animate-in zoom-in group relative">
                        <img src={selectedItems[activeItemIndex].modele_image?.startsWith('data:') ? selectedItems[activeItemIndex].modele_image : api.getProxyUrl(selectedItems[activeItemIndex].modele_image)} className="w-full h-full object-cover" alt="preview" />
                        <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                           <i className="ri-check-line text-white text-2xl drop-shadow-md" />
                        </div>
                     </div>
                )}
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            {CATEGORIES.map(c => (
              <button
                key={c}
                onClick={() => setActiveCateg(c)}
                className={`whitespace-nowrap px-3 py-1.5 rounded-full text-[10px] font-bold uppercase transition-colors ${
                  activeCateg === c ? 'bg-foret-500 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {loading ? (
              <div className="flex justify-center py-10 text-gray-400 animate-pulse text-xs">Chargement du catalogue...</div>
          ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[300px] overflow-y-auto pr-1">
                  {filtered.map(m => (
                    <button
                        key={m.id}
                        onClick={() => onSelect(m.id, m.image)}
                        className={`relative aspect-[3/4] rounded-xl overflow-hidden border-2 transition-all group ${
                            selectedItems[activeItemIndex].modele_id === m.id ? 'border-foret-500 ring-2 ring-foret-100' : 'border-gray-50 hover:border-foret-200'
                        }`}
                    >
                        <img 
                            src={api.getProxyUrl(m.image) || 'https://via.placeholder.com/200'} 
                            className="w-full h-full object-cover object-top" 
                            alt={m.nom} 
                        />
                        {selectedItems[activeItemIndex].modele_id === m.id && (
                            <div className="absolute inset-0 bg-foret-500/10 flex items-center justify-center">
                                <i className="ri-check-line text-white bg-foret-500 rounded-full p-1 text-lg shadow-lg" />
                            </div>
                        )}
                        <div className="absolute bottom-0 inset-x-0 p-1 bg-gradient-to-t from-black/60 to-transparent">
                            <p className="text-[9px] font-bold text-white truncate">{m.nom}</p>
                        </div>
                    </button>
                  ))}
              </div>
          )}
      </div>
    </div>
  );
}
